import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { 
  MapPin, 
  Plus, 
  Edit, 
  Trash2, 
  Navigation, 
  Home, 
  Building, 
  Star,
  Check,
  X,
  AlertCircle
} from 'lucide-react';
import LocationService from '../services/locationService';

const AddressManager = ({ onAddressSelect, selectedAddressId, showSelector = false }) => {
  const [addresses, setAddresses] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    label: '',
    flatNumber: '',
    houseNumber: '',
    apartment: '',
    street: '',
    locality: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    landmark: '',
    isDefault: false
  });

  // Load addresses from localStorage (in real app, from API)
  useEffect(() => {
    const savedAddresses = localStorage.getItem('geeth365_addresses');
    if (savedAddresses) {
      setAddresses(JSON.parse(savedAddresses));
    }
  }, []);

  // Save addresses to localStorage
  const saveAddresses = (newAddresses) => {
    setAddresses(newAddresses);
    localStorage.setItem('geeth365_addresses', JSON.stringify(newAddresses));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handlePincodeChange = async (pincode) => {
    handleInputChange('pincode', pincode);
    
    if (pincode.length === 6) {
      setLoading(true);
      try {
        const locationData = await LocationService.getLocationFromPincode(pincode);
        setFormData(prev => ({
          ...prev,
          city: locationData.city,
          state: locationData.state,
          locality: locationData.locality || prev.locality,
          country: locationData.country
        }));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleGPSLocation = async () => {
    setLoading(true);
    try {
      const position = await LocationService.getCurrentLocation();
      const address = await LocationService.reverseGeocode(position.lat, position.lng);
      
      setFormData(prev => ({
        ...prev,
        street: address.street,
        locality: address.locality,
        city: address.city,
        state: address.state,
        country: address.country,
        pincode: address.pincode
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const required = ['flatNumber', 'street', 'locality', 'city', 'state', 'pincode', 'label'];
    const missing = required.filter(field => !formData[field].trim());
    
    if (missing.length > 0) {
      setError(`Please fill in: ${missing.join(', ')}`);
      return false;
    }

    if (!LocationService.isValidPincode(formData.pincode)) {
      setError('Please enter a valid 6-digit pincode');
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const newAddress = {
      id: editingAddress ? editingAddress.id : Date.now(),
      ...formData,
      formattedAddress: `${formData.flatNumber}, ${formData.street}, ${formData.locality}, ${formData.city}, ${formData.state} ${formData.pincode}`,
      createdAt: editingAddress ? editingAddress.createdAt : new Date().toISOString()
    };

    let newAddresses;
    if (editingAddress) {
      newAddresses = addresses.map(addr => addr.id === editingAddress.id ? newAddress : addr);
    } else {
      // If this is the first address or marked as default, make it default
      if (addresses.length === 0 || formData.isDefault) {
        newAddresses = addresses.map(addr => ({ ...addr, isDefault: false }));
        newAddress.isDefault = true;
        newAddresses.push(newAddress);
      } else {
        newAddresses = [...addresses, newAddress];
      }
    }

    saveAddresses(newAddresses);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      label: '',
      flatNumber: '',
      houseNumber: '',
      apartment: '',
      street: '',
      locality: '',
      city: '',
      state: '',
      country: 'India',
      pincode: '',
      landmark: '',
      isDefault: false
    });
    setShowAddForm(false);
    setEditingAddress(null);
    setError('');
  };

  const handleEdit = (address) => {
    setFormData(address);
    setEditingAddress(address);
    setShowAddForm(true);
  };

  const handleDelete = (addressId) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      const newAddresses = addresses.filter(addr => addr.id !== addressId);
      saveAddresses(newAddresses);
    }
  };

  const handleSetDefault = (addressId) => {
    const newAddresses = addresses.map(addr => ({
      ...addr,
      isDefault: addr.id === addressId
    }));
    saveAddresses(newAddresses);
  };

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya',
    'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim',
    'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi'
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-gray-900">Delivery Addresses</h2>
        {!showAddForm && (
          <Button 
            onClick={() => setShowAddForm(true)}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Address
          </Button>
        )}
      </div>

      {/* Add/Edit Address Form */}
      {showAddForm && (
        <Card className="border-orange-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <MapPin className="w-5 h-5 text-orange-600" />
              <span>{editingAddress ? 'Edit Address' : 'Add New Address'}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Address Label */}
              <div>
                <Label htmlFor="label">Address Label *</Label>
                <Select value={formData.label} onValueChange={(value) => handleInputChange('label', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select address type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Home">🏠 Home</SelectItem>
                    <SelectItem value="Office">🏢 Office</SelectItem>
                    <SelectItem value="Other">📍 Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* GPS Button and Pincode */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Auto-fill with GPS</Label>
                  <Button 
                    type="button"
                    onClick={handleGPSLocation}
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    {loading ? 'Getting Location...' : 'Use GPS Location'}
                  </Button>
                </div>
                <div>
                  <Label htmlFor="pincode">Pincode *</Label>
                  <Input
                    id="pincode"
                    value={formData.pincode}
                    onChange={(e) => handlePincodeChange(e.target.value)}
                    placeholder="Enter 6-digit pincode"
                    maxLength={6}
                  />
                </div>
              </div>

              {/* Flat/House Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="flatNumber">Flat/Floor Number *</Label>
                  <Input
                    id="flatNumber"
                    value={formData.flatNumber}
                    onChange={(e) => handleInputChange('flatNumber', e.target.value)}
                    placeholder="e.g., 2B, Ground Floor"
                  />
                </div>
                <div>
                  <Label htmlFor="houseNumber">House Number</Label>
                  <Input
                    id="houseNumber"
                    value={formData.houseNumber}
                    onChange={(e) => handleInputChange('houseNumber', e.target.value)}
                    placeholder="e.g., 123, H.No. 45"
                  />
                </div>
                <div>
                  <Label htmlFor="apartment">Apartment/Society</Label>
                  <Input
                    id="apartment"
                    value={formData.apartment}
                    onChange={(e) => handleInputChange('apartment', e.target.value)}
                    placeholder="e.g., ABC Apartments"
                  />
                </div>
              </div>

              {/* Street and Locality */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="street">Street/Road *</Label>
                  <Input
                    id="street"
                    value={formData.street}
                    onChange={(e) => handleInputChange('street', e.target.value)}
                    placeholder="e.g., MG Road, Main Street"
                  />
                </div>
                <div>
                  <Label htmlFor="locality">Locality/Area *</Label>
                  <Input
                    id="locality"
                    value={formData.locality}
                    onChange={(e) => handleInputChange('locality', e.target.value)}
                    placeholder="e.g., Banjara Hills, Sector 1"
                  />
                </div>
              </div>

              {/* City, State, Country */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="e.g., Hyderabad"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Select value={formData.state} onValueChange={(value) => handleInputChange('state', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {indianStates.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    disabled
                    className="bg-gray-50"
                  />
                </div>
              </div>

              {/* Landmark */}
              <div>
                <Label htmlFor="landmark">Landmark (Optional)</Label>
                <Textarea
                  id="landmark"
                  value={formData.landmark}
                  onChange={(e) => handleInputChange('landmark', e.target.value)}
                  placeholder="e.g., Near Apollo Hospital, Opposite Metro Station"
                  rows={2}
                />
              </div>

              {/* Default Address */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <Label htmlFor="isDefault" className="text-sm">
                  Set as default delivery address
                </Label>
              </div>

              {/* Form Actions */}
              <div className="flex space-x-4 pt-4">
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {editingAddress ? 'Update Address' : 'Save Address'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={resetForm}
                  className="border-gray-300"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Existing Addresses */}
      <div className="space-y-4">
        {addresses.length === 0 ? (
          <Card className="border-dashed border-gray-300">
            <CardContent className="p-8 text-center">
              <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No delivery addresses</h3>
              <p className="text-gray-600 mb-4">Add your first address to start ordering medicines and lab tests</p>
              <Button 
                onClick={() => setShowAddForm(true)}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Address
              </Button>
            </CardContent>
          </Card>
        ) : (
          addresses.map((address) => (
            <Card 
              key={address.id} 
              className={`${
                showSelector && selectedAddressId === address.id
                  ? 'border-orange-400 bg-orange-50'
                  : 'border-gray-200'
              } ${showSelector ? 'cursor-pointer hover:border-orange-300' : ''}`}
              onClick={() => showSelector && onAddressSelect && onAddressSelect(address)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="flex items-center space-x-2">
                        {address.label === 'Home' && <Home className="w-4 h-4 text-green-600" />}
                        {address.label === 'Office' && <Building className="w-4 h-4 text-blue-600" />}
                        {address.label === 'Other' && <MapPin className="w-4 h-4 text-gray-600" />}
                        <span className="font-medium text-gray-900">{address.label}</span>
                      </div>
                      
                      {address.isDefault && (
                        <Badge className="bg-green-100 text-green-700">
                          <Star className="w-3 h-3 mr-1" />
                          Default
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-gray-700 mb-2">
                      {address.formattedAddress}
                    </p>
                    
                    {address.landmark && (
                      <p className="text-sm text-gray-500">
                        Landmark: {address.landmark}
                      </p>
                    )}
                  </div>
                  
                  {!showSelector && (
                    <div className="flex space-x-2 ml-4">
                      {!address.isDefault && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSetDefault(address.id)}
                          className="text-xs"
                        >
                          Set Default
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(address)}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(address.id)}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AddressManager;